import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { ToastrService } from 'ngx-toastr';
import { HeaderService } from 'app/services/header/header.service';
import {
  daySeriesValue,
  enumerateDaysInclusive,
  formatTnd,
  seriesByDay,
  shortDayLabel,
} from 'app/services/logiciel-caisse/logiciel-caisse-dashboard-stats.service';
import { LogicielCaisseAuthService } from 'app/services/logiciel-caisse/logiciel-caisse-auth.service';
import { LogicielCaissePosFiltersComponent } from '../logiciel-caisse/shared/logiciel-caisse-pos-filters/logiciel-caisse-pos-filters.component';
import { LcPosListFilters } from '../logiciel-caisse/shared/logiciel-caisse-pos-list.types';
import {
  AchatDashboardBundle,
  AchatDashboardService,
  ErpAchatDashboardPayload,
  FacturesFournisseurGlobalesStats,
} from 'app/services/dashboard/achat-dashboard.service';
import { getStationIdFromLocalStorage } from 'app/services/parametre-document/parametre-document-state.service';
import { ReasortTransfertCounts } from 'app/services/logiciel-caisse/logiciel-caisse-reasort-transfert.service';
import {
  dashboardBarLegendPlugin,
  dashboardChartLayout,
  dashboardLineDataset,
  dashboardLineLegendPlugin,
} from 'app/services/dashboard/dashboard-chart-present.util';

type LineChartOpts = ChartConfiguration<'line'>['options'];
type BarChartOpts = ChartConfiguration<'bar'>['options'];

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, AfterViewInit {
  @ViewChild('filters') filtersCmp?: LogicielCaissePosFiltersComponent;

  dateFrom = '';
  dateTo = '';
  loading = false;
  stationId = 0;
  lcConfigured = false;

  erp: ErpAchatDashboardPayload | null = null;
  facturesGlobales: FacturesFournisseurGlobalesStats | null = null;
  reassort: ReasortTransfertCounts | null = null;

  formatTnd = formatTnd;

  erpAchatChart: ChartConfiguration<'line'> = this.emptyLineChart('Documents achat');
  facturesPaiementChart: ChartConfiguration<'bar'> = this.emptyBarChart('Factures fournisseur');

  constructor(
    private readonly headerService: HeaderService,
    private readonly dashboardService: AchatDashboardService,
    private readonly lcAuth: LogicielCaisseAuthService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.headerService.setConfig({ title: 'Tableau de bord achat', buttons: [] });
    this.stationId = getStationIdFromLocalStorage() ?? 0;
    this.lcConfigured = !!this.lcAuth.getSession()?.token;
  }

  ngAfterViewInit(): void {
    this.filtersCmp?.setWarehouseOptions([]);
  }

  onFiltersChange(filters: LcPosListFilters): void {
    this.dateFrom = filters.dateDebut;
    this.dateTo = filters.dateFin;
    if (this.dateFrom && this.dateTo && this.stationId > 0) {
      this.loadStats();
    }
  }

  private loadStats(): void {
    if (!this.stationId) {
      this.toastr.error('Station non sélectionnée. Reconnectez-vous ou choisissez une station.');
      return;
    }
    this.loading = true;
    this.dashboardService
      .fetchBundle({
        stationId: this.stationId,
        dateFrom: this.dateFrom,
        dateTo: this.dateTo,
        includeReassort: this.lcConfigured,
      })
      .subscribe({
        next: (bundle) => {
          this.applyBundle(bundle);
          this.loading = false;
        },
        error: (err: Error) => {
          this.toastr.error(err.message || 'Erreur chargement tableau de bord');
          this.loading = false;
        },
      });
  }

  private applyBundle(bundle: AchatDashboardBundle): void {
    this.erp = bundle.erp;
    this.facturesGlobales = bundle.facturesGlobales;
    this.reassort = bundle.reassort;
    if (!this.erp && !this.facturesGlobales) {
      this.toastr.warning('Aucune donnée achat disponible pour cette période.');
    }
    this.buildCharts();
  }

  private buildCharts(): void {
    const days = enumerateDaysInclusive(this.dateFrom, this.dateTo);
    const labels = days.map(shortDayLabel);

    if (this.erp?.series) {
      const cf = seriesByDay(this.erp.series.commande_fournisseur);
      const br = seriesByDay(this.erp.series.bon_reception);
      const ff = seriesByDay(this.erp.series.facture_fournisseur);
      const rf = seriesByDay(this.erp.series.retour_fournisseur);
      const af = seriesByDay(this.erp.series.avoir_fournisseur);

      this.erpAchatChart = {
        type: 'line',
        data: {
          labels,
          datasets: [
            dashboardLineDataset(
              'Commande fournisseur',
              days.map((d) => daySeriesValue(cf, d, 'count')),
              '#7367f0'
            ),
            dashboardLineDataset(
              'Bon réception',
              days.map((d) => daySeriesValue(br, d, 'count')),
              '#28c76f'
            ),
            dashboardLineDataset(
              'Facture fournisseur',
              days.map((d) => daySeriesValue(ff, d, 'count')),
              '#ff9f43'
            ),
            dashboardLineDataset(
              'Retour fournisseur',
              days.map((d) => daySeriesValue(rf, d, 'count')),
              '#ea5455'
            ),
            dashboardLineDataset(
              'Avoir fournisseur',
              days.map((d) => daySeriesValue(af, d, 'count')),
              '#00cfe8'
            ),
          ],
        },
        options: this.lineOptions(),
      };
    }

    if (this.facturesGlobales) {
      const g = this.facturesGlobales;
      this.facturesPaiementChart = {
        type: 'bar',
        data: {
          labels: ['Montants TTC (toutes factures)'],
          datasets: [
            {
              label: 'Payées',
              data: [g.factures_payees?.total_ttc ?? 0],
              backgroundColor: 'rgba(40, 199, 111, 0.85)',
              borderColor: '#28c76f',
              borderRadius: 4,
            },
            {
              label: 'À payer',
              data: [g.factures_a_payer?.total_ttc ?? 0],
              backgroundColor: 'rgba(234, 84, 85, 0.85)',
              borderColor: '#ea5455',
              borderRadius: 4,
            },
          ],
        },
        options: this.barOptions(true),
      };
    }
  }

  private lineOptions(): LineChartOpts {
    return {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      layout: dashboardChartLayout,
      plugins: dashboardLineLegendPlugin(),
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxTicksLimit: 12, color: '#6e6b7b' },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(200, 200, 200, 0.25)' },
          ticks: { color: '#6e6b7b', precision: 0 },
        },
      },
    };
  }

  private barOptions(currencyY: boolean): BarChartOpts {
    return {
      responsive: true,
      maintainAspectRatio: false,
      layout: dashboardChartLayout,
      plugins: dashboardBarLegendPlugin(),
      scales: {
        x: {
          grid: { display: false },
          ticks: { maxTicksLimit: 8, color: '#6e6b7b' },
        },
        y: {
          beginAtZero: true,
          grid: { color: 'rgba(200, 200, 200, 0.25)' },
          ticks: {
            color: '#6e6b7b',
            callback: currencyY
              ? (v) => `${Number(v).toLocaleString('fr-TN', { maximumFractionDigits: 0 })}`
              : undefined,
          },
        },
      },
    };
  }

  private emptyLineChart(label: string): ChartConfiguration<'line'> {
    return {
      type: 'line',
      data: { labels: [], datasets: [{ label, data: [] }] },
      options: this.lineOptions(),
    };
  }

  private emptyBarChart(label: string): ChartConfiguration<'bar'> {
    return {
      type: 'bar',
      data: { labels: [], datasets: [{ label, data: [] }] },
      options: this.barOptions(false),
    };
  }
}
